/**
 * Keeping the wizard's deep-link params true to what is actually selected.
 *
 * The params are read on the way in (`usePreselectFromUrl`) and carried across
 * steps (`preserveRegistrationParams`). This writes them back — but only ones
 * the URL already carries.
 *
 * Nothing here is needed to move a choice forward: the session holds the
 * division, team and role, and each step restores from it. The params matter
 * only because a URL that names them is shareable and may be copied onward, so
 * an id left pointing at something the reader has since changed would send the
 * next person to a division, team or role nobody chose. A reader who arrived
 * without a param gets a clean URL and keeps it.
 */

import {
  REGISTRATION_DIVISION_PARAM,
  REGISTRATION_ROLE_PARAM,
  REGISTRATION_TEAM_PARAM,
} from '@sportninja/common/features/registration/registrationUrlParams'

export interface RegistrationSelection {
  /** The team's own id — what `team_id` carries. */
  teamId?: string | null
  /**
   * The division's SCHEDULE uid, which is what `division_id` carries — NOT the
   * division's own `id`. See `findPreselectableDivision`.
   */
  divisionScheduleId?: string | null
  /** The chosen role: what `type` carries. */
  role?: string | null
}

/**
 * Which params to rewrite. Each is updated only when the URL already has it, so
 * this never introduces one the reader did not arrive with.
 *
 * Required rather than defaulted: a caller that forgot it would silently write
 * some params and not others, which is exactly the bug this guards against.
 */
export interface SelectionParamPresence {
  team?: boolean
  division?: boolean
  role?: boolean
}

/**
 * The search string with the present params updated to the current selection.
 *
 * Every other param is untouched: one-shot params such as `nreg` are dropped a
 * step later by `preserveRegistrationParams`, not here.
 *
 * A present param whose selection is now empty is REMOVED rather than left
 * stale — a reader who backs up and deselects must not be sent forward into the
 * old choice.
 */
export const withSelectionParams = (
  search: string | undefined,
  { teamId, divisionScheduleId, role }: RegistrationSelection,
  present: SelectionParamPresence
): string => {
  const params = new URLSearchParams(search ?? '')

  const apply = (
    isPresent: boolean | undefined,
    key: string,
    value?: string | null
  ) => {
    if (!isPresent) {
      return
    }
    if (value) {
      params.set(key, value)
    } else {
      params.delete(key)
    }
  }

  apply(present.team, REGISTRATION_TEAM_PARAM, teamId)
  apply(present.division, REGISTRATION_DIVISION_PARAM, divisionScheduleId)
  apply(present.role, REGISTRATION_ROLE_PARAM, role)

  const qs = params.toString()
  return qs ? `?${qs}` : ''
}
